import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { dateConfig } from "src/common/util/DateConfig";
import { IsLoading } from "../../common/components/AppState/IsLoading";
import { useAppDispatch } from "../../common/state/hooks";
import { languageConfig } from "../../common/util/langConfig";
import { prettifyFileSizeInBytes } from "../../common/util/prettifyFileSizeInBytes";
import { getCurrentUser } from "../../features/profile/state/profileSlice";
import {
  getCurrentInteractionElements,
  getCurrentInteractions,
  getCurrentProject,
  getProjectSize,
} from "../../features/projects/state/projectsSlice";
import {
  fetchGptConversationForProject,
  setActiveGptConversation,
} from "../../features/chatbot/state/chatbotSlice";
import { ILanguage, IGptConversation } from "../../model/model";
import InformationComponent from "../ProjectGroups/InformationComponent";
import Badge from "../../common/components/Badge/Badge";
import InteractionsList from "./InteractionsList";
import ReactMarkdown from "react-markdown";
import PlanPreview from "../../features/chatbot/components/PlanPreview";
import { ButtonText } from "src/common/components/Button";

export const PROJECT_ELEMENTS_COMPONENT_ID = "projectElements";
export const PROJECT_SIZE_COMPONENT_ID = "projectSize";
export const PROJECT_ORIGIN_CONVERSATION_COMPONENT_ID = "projectOriginConversation";

const ProjectInfo = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector(getCurrentUser);
  const currentLocale = languageConfig((currentUser?.preferred_language as ILanguage)?.name);
  const currentProject = useSelector(getCurrentProject);
  const getElementsInteractions = useSelector(getCurrentInteractionElements);
  const [originConversation, setOriginConversation] = useState<IGptConversation | null>(null);

  useEffect(() => {
    if (currentProject?.id) {
      dispatch(
        getCurrentInteractions({
          projectId: currentProject.id,
          componentId: PROJECT_ELEMENTS_COMPONENT_ID,
        }),
      );
      dispatch(
        getProjectSize({
          projectId: currentProject.id,
          componentId: PROJECT_SIZE_COMPONENT_ID,
        }),
      );
    }
  }, [dispatch, currentProject?.id]);

  // Resolve the originating gpt-conversation only when the project actually
  // has a prompt-synthesis snapshot — legacy projects without an
  // aiPromptBasis can't have come from plan-mode, so there's no conversation
  // to look up. The thunk pipes through loaderHandleMiddleware so the
  // IsLoading wrapper around the button row drives the spinner automatically.
  useEffect(() => {
    setOriginConversation(null);
    if (!currentProject?.id || !currentProject?.metadata?.aiPromptBasis) return;
    dispatch(
      fetchGptConversationForProject({
        projectId: currentProject.id,
        componentId: PROJECT_ORIGIN_CONVERSATION_COMPONENT_ID,
      }),
    ).then((action) => {
      if (fetchGptConversationForProject.fulfilled.match(action) && action.payload) {
        setOriginConversation(action.payload as IGptConversation);
      }
    });
  }, [dispatch, currentProject?.id, currentProject?.metadata?.aiPromptBasis]);

  const handleViewOriginConversation = () => {
    if (!originConversation) return;
    dispatch(setActiveGptConversation(originConversation));
    navigate("/chat/scenario-helper");
  };

  const currentProjectCreator = currentProject;
  const createdByTranslate = currentProjectCreator?.created_by
    ? currentProjectCreator.created_by
    : t("general.noCreator");

  const getDateCurrentLocale = (date: string) =>
    new Date(date).toLocaleDateString(dateConfig(currentLocale));

  const primaryLanguageId = currentProject?.language?.id;
  const enabledLanguages = currentProject?.enabled_languages ?? [];
  const languagesToShow =
    currentProject?.language && !enabledLanguages.some((l) => l.id === primaryLanguageId)
      ? [currentProject.language, ...enabledLanguages]
      : enabledLanguages;

  const languagesNode =
    languagesToShow.length > 0 ? (
      <span className="flex flex-wrap">
        {languagesToShow.map((lang) => {
          const isPrimary = lang.id === primaryLanguageId;
          return (
            <Badge
              key={lang.id}
              text={
                lang.IETF_BCP_47
                  ? t(`general.languages.IETF_BCP_47.${lang.IETF_BCP_47}`)
                  : lang.name
              }
              className="p-2 mr-1 mb-1 select-none whitespace-nowrap"
              color={isPrimary ? "bg-green-500" : "bg-gray-500"}
              textColor={isPrimary ? "text-green-100" : "text-gray-100"}
            />
          );
        })}
      </span>
    ) : (
      <span className="italic">-</span>
    );

  return (
    <IsLoading componentId={PROJECT_SIZE_COMPONENT_ID} showSuccess={false}>
      <div className="relative grow w-full px-4 xl:px-0 mx-auto overflow-x-auto personalize-scroll-visible">
        <div className="ml-4">
          <h2 className="text-2xl font-medium mb-2 mt-4">Description</h2>
          <h3 className="py-3">{t("general.informationModule")}</h3>

          <InformationComponent
            title={t("general.moduleName")}
            moduleName={currentProject?.name || ""}
          />
          <InformationComponent title={t("general.enabledLanguages")} moduleName={languagesNode} />
          <InformationComponent
            title={t("general.weight")}
            moduleName={prettifyFileSizeInBytes(
              getElementsInteractions?.sizeInMbs || 0,
              currentLocale,
            )}
            infoTooltipText={t("tooltip.mediasWeightDetail")}
          />
          {currentProjectCreator?.creator && (
            <InformationComponent
              title={t("general.roles.creator")}
              moduleName={createdByTranslate}
            />
          )}
          <InformationComponent
            title={t("general.createdAt")}
            moduleName={getDateCurrentLocale(currentProject?.createdAt || "")}
          />
          <InformationComponent
            title={t("general.updatedAt")}
            moduleName={getDateCurrentLocale(currentProject?.updatedAt || "")}
          />
          {currentProject?.cloned_from?.name && (
            <InformationComponent
              title={t("general.duplicatedFrom")}
              moduleName={currentProject.cloned_from.name}
            />
          )}

          {currentProject?.metadata?.aiPromptBasis && (
            <div className="mt-2 border-gray-300">
              <div className="w-full flex flex-col sm:flex-row py-2 px-2 xl:py-3 xl:px-4 border-t border-gray-300">
                <p className="w-full sm:w-1/3 text-sm text-center sm:text-left">
                  {t("pages.projects.aiPromptBasis")}
                </p>
                <div className="w-full">
                  <IsLoading
                    componentId={PROJECT_ORIGIN_CONVERSATION_COMPONENT_ID}
                    showSuccess={false}
                  >
                    <ButtonText
                      text={t("pages.projects.aiPromptBasisViewConversation")}
                      onClick={handleViewOriginConversation}
                      disabled={!originConversation}
                    />
                  </IsLoading>
                  <div className="mt-3">
                    {originConversation?.conversation_metadata?.plan ? (
                      <PlanPreview plan={originConversation.conversation_metadata.plan} />
                    ) : (
                      <div className="prose max-w-full break-words">
                        <ReactMarkdown>{currentProject.metadata.aiPromptBasis}</ReactMarkdown>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="relative grow w-full px-4 xl:px-0 mx-auto">
            <h2 className="text-2xl font-medium mb-2 mt-4 xl:mt-8">Elements</h2>
            <h3 className="py-3">{t("general.interactionsElements")}</h3>
            <InteractionsList interactions={getElementsInteractions?.interactions} />
          </div>
        </div>
      </div>
    </IsLoading>
  );
};

export default ProjectInfo;
